// Maps the technical SharePoint site slug stored in the index `sitename`
// field to a human-readable label FOR DISPLAY ONLY. The raw slug must
// still be used for all filter values, request bodies, and the question
// suffix sent to the backend — never pass the mapped label back.
const SITE_NAME_OVERRIDES: Record<string, string> = {
    "lts_detau": "LTS Tauberbischofsheim",
    "lts_deiln": "LTS Illertissen",
    "lts_defbg": "LTS Malterdingen",
    "lts_dealf": "LTS Alfeld",
    "lts_gbagn": "LTS United Kingdom",
    "lts_denun": "LTS Nürtingen",
    "lts_denum": "LTS Nürtingen", // alias — some sources spell the slug "denum"
    "lts_usxjk": "LTS USA",
    "lts_lufoe": "LTS Luxembourg",
    "WGRP-WMCDokumentation": "WMC Dokumentation",
};

export const displaySiteName = (slug: string): string => {
    if (!slug) return slug;
    if (SITE_NAME_OVERRIDES[slug]) return SITE_NAME_OVERRIDES[slug];
    // Unknown lts_<code> site: fall back to "LTS <Code>" so a newly-added
    // site still reads sensibly until it gets an explicit mapping above.
    const m = slug.match(/^lts_(.+)$/i);
    if (m) {
        const code = m[1];
        return "LTS " + code.charAt(0).toUpperCase() + code.slice(1);
    }
    return slug; // unknown non-lts slug: show as-is
};

// Only the site facet should be prettified; every other facet renders raw.
export const displayFacetValue = (facetKey: string, value: string): string =>
    facetKey === "sitename" ? displaySiteName(value) : value;

// Prettify site slugs that appear inside the trailing " [ ... ]" filter
// suffix that Chat appends to a user question (e.g.
// "Wie stelle ich Sensor X ein? [sitename: lts_deiln]"). DISPLAY ONLY —
// the underlying message string sent to and stored by the backend keeps
// the raw slug. Only slug-shaped tokens inside the final bracket group are
// rewritten, so real question text is never touched.
export const prettifyMessageForDisplay = (message: string): string => {
    if (!message) return message;
    const m = message.match(/^([\s\S]*)\s\[([^\]]*)\]$/);
    if (!m) return message;
    const [, head, inside] = m;
    const prettyInside = inside.replace(
        /lts_[a-z0-9]+|WGRP-WMCDokumentation/gi,
        (slug) => displaySiteName(slug),
    );
    return `${head} [${prettyInside}]`;
};
